import { createElement } from 'react';
import {
  ActivationPackWithTarget,
  GeneratedAsset,
  ASSET_TYPE_INFO,
} from '@/types/activation/activation-pack';
import { PosterDesign } from '@/types/activation/poster';
import { resolveLookbookBrand } from '@/components/activation/lookbook/lookbookBrand';
import { PosterDocument } from '@/components/activation/poster/PosterDocument';
import { PosterPageFrame, POSTER_PAGE_W, POSTER_PAGE_H } from '@/components/activation/poster/PosterPageFrame';

const sanitizeFilename = (name: string) =>
  name.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '') || 'genie';

const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));
const PRINT_RENDER_SCALE = 300 / 96;

/**
 * Verify a logo loads cleanly under CORS before it's ever rendered into the
 * page — a logo host that doesn't send `Access-Control-Allow-Origin` taints
 * html2canvas's output canvas and `canvas.toDataURL()` then throws, aborting
 * the whole export. Mirrors `lookbookPdf.ts`'s preloadLogo. Resolves the URL
 * only on a clean load; callers should swap it for `null` otherwise so the
 * brand mark falls back to its initial-tile placeholder.
 */
const preloadLogo = (src: string | null): Promise<string | null> =>
  new Promise((resolve) => {
    if (!src) return resolve(null);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(src);
    img.onerror = () => resolve(null);
    img.src = src;
    setTimeout(() => resolve(null), 2500);
  });

/**
 * Render the poster React document offscreen, rasterize each A4 page with
 * html2canvas, and assemble a jsPDF — one page per design, print-ready.
 * Mirrors `lookbookPdf.ts` so the in-app gallery and the PDF stay
 * pixel-identical (same components).
 */
export async function generatePosterPackPdf(pack: ActivationPackWithTarget): Promise<GeneratedAsset> {
  const brand = resolveLookbookBrand(pack);

  const [{ default: html2canvas }, { default: jsPDF }, { createRoot }] = await Promise.all([
    import('html2canvas'),
    import('jspdf'),
    import('react-dom/client'),
  ]);

  const container = document.createElement('div');
  container.style.cssText =
    `position:absolute; left:-10000px; top:0; width:${POSTER_PAGE_W}px; background:#ffffff;`;
  document.body.appendChild(container);
  const root = createRoot(container);

  try {
    const safeBrand = { ...brand, logoUrl: await preloadLogo(brand.logoUrl) };
    root.render(createElement(PosterDocument, { brand: safeBrand }));

    if (document.fonts?.ready) await document.fonts.ready;
    await wait(450);

    const pages = Array.from(container.querySelectorAll<HTMLElement>('[data-poster-page]'));

    const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4', compress: true });
    const PT_W = 595.28;
    const PT_H = 841.89;

    for (let i = 0; i < pages.length; i++) {
      const canvas = await html2canvas(pages[i], {
        scale: PRINT_RENDER_SCALE,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
        width: POSTER_PAGE_W,
        height: POSTER_PAGE_H,
        windowWidth: POSTER_PAGE_W,
        windowHeight: POSTER_PAGE_H,
      });
      const data = canvas.toDataURL('image/png');
      if (i > 0) pdf.addPage();
      pdf.addImage(data, 'PNG', 0, 0, PT_W, PT_H, undefined, 'FAST');
    }

    return {
      type: 'poster_pack_pdf',
      label: ASSET_TYPE_INFO.poster_pack_pdf.label,
      description: 'A4 poster designs rendered at 300 DPI for production handoff',
      data: pdf.output('blob'),
      filename: `${sanitizeFilename(pack.name)}-posters.pdf`,
      mimeType: 'application/pdf',
    };
  } finally {
    root.unmount();
    container.remove();
  }
}

/**
 * Render just ONE poster design to a single-page A4 PDF blob — used when
 * someone selects individual posters (not the whole pack) to add to their
 * print order. Same rasterization path as the full document.
 */
export async function generatePosterDesignPdf(
  pack: ActivationPackWithTarget,
  design: PosterDesign,
): Promise<Blob> {
  const brand = resolveLookbookBrand(pack);

  const [{ default: html2canvas }, { default: jsPDF }, { createRoot }] = await Promise.all([
    import('html2canvas'),
    import('jspdf'),
    import('react-dom/client'),
  ]);

  const container = document.createElement('div');
  container.style.cssText =
    `position:absolute; left:-10000px; top:0; width:${POSTER_PAGE_W}px; background:#ffffff;`;
  document.body.appendChild(container);
  const root = createRoot(container);

  try {
    const safeBrand = { ...brand, logoUrl: await preloadLogo(brand.logoUrl) };
    root.render(createElement(PosterPageFrame, { brand: safeBrand, design }));

    if (document.fonts?.ready) await document.fonts.ready;
    await wait(350);

    const canvas = await html2canvas(container, {
      scale: PRINT_RENDER_SCALE,
      useCORS: true,
      backgroundColor: '#ffffff',
      logging: false,
      width: POSTER_PAGE_W,
      height: POSTER_PAGE_H,
      windowWidth: POSTER_PAGE_W,
      windowHeight: POSTER_PAGE_H,
    });

    const PT_W = 595.28;
    const PT_H = 841.89;
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4', compress: true });
    pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, PT_W, PT_H, undefined, 'FAST');
    return pdf.output('blob');
  } finally {
    root.unmount();
    container.remove();
  }
}
